import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Mail, Save, Search } from 'lucide-react';
import BackArrow from '../components/ui/BackArrow';
import {
  GENERAL_REMARKS_MAX,
  PRICES_EXCLUDE_VAT,
  assignVendorNotesMessage,
  assignVendorToPurchaseOrder,
  assignVendorToPurchaseOrderErrorMessage,
  convertPurchaseOrderToCases,
  convertToCasesDialog,
  convertToCasesResultMessage,
  convertToCasesRoundUpOffers,
  decidePackRoundingErrorMessage,
  downloadVendorPurchaseOrder,
  fetchVendorPurchaseOrderById,
  packDecisionPendingNote,
  sendVendorPurchaseOrder,
  unpricedLineNote,
  updateVendorPurchaseOrder,
  updateVendorPurchaseOrderErrorMessage,
  vendorPoSendErrorMessage,
  type ConvertToCasesRoundUp,
  type VendorPoPackConversion,
  type VendorPurchaseOrderDto,
} from '../lib/vendorPurchaseOrderApi';
import { ActionDialog } from '../components/ActionDialog';
import type { DialogContent } from '../utils/errorPresentation';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import LoadingState from '../components/LoadingState';
import StatusBadge from '../components/StatusBadge';
import SendPurchaseOrderModal from '../components/SendPurchaseOrderModal';
import { emailAlreadyWent } from '../lib/emailRefusal';
import ExportDropdown from '../components/ExportDropdown';
import { VendorSelect, type VendorSelection } from '../components/VendorSelect';
import { ReconciliationPanel } from '../features/reconciliation/ReconciliationPanel';
import { ChainRail } from '../features/traceability/ChainRail';
import { CHAIN_PATH } from '../features/traceability/chainRef';
import { PendingPackControl } from '../features/vendorPurchaseOrders/PackConversion';
import { isPackApplied, packContentsNote, packPiecesNote, poLineAmountProblem, wholePackCount, type PoLineAmountProblem } from '../utils/packConversion';
import { refLabel } from '../features/traceability/chainRef';
import { usePermission } from '../hooks/usePermission';
import { formatDate as fmtDate, formatDateTime as fmtDateTime, storedDay } from "../utils/dateFormat";
import { ceilPrice, currencyPrefix, formatCurrency, moneyInputText } from '../utils/priceUtils';
import DateInput from "../components/DateInput";

const VENDOR_PO_DETAIL_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>Vendor Purchase Order detail: vendor information and line items in TT$.</p>
    <p className="font-medium">You can view:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Vendor contact details</li>
      <li>Line items with quantities and unit prices</li>
    </ul>
  </div>
);

const BORDER = '#D7DBD9';
const EMPTY_FALLBACK = '-';

type EditablePoItem = {
  lineNumber?: number;
  // The order's wording: read-only here, never sent (the order owns it).
  itemName: string;
  // D119.8: the name the supplier SEES (its own name for the product, else the order's wording).
  // The name box edits it; saved as `supplierItemName`.
  printedName: string;
  itemDescription?: string;
  itemCode: string;
  quantity: string;
  uom: string;
  unitPrice: string;
  totalPrice: string;
  remarks: string;
  // D81, server-owned: its order line left after the PO was sent — out of every total.
  removed: boolean;
};

type EditablePo = {
  _id?: string;
  poNumber: string;
  vesselName: string;
  hideVesselPrefix: boolean;
  generalRemarks: string;
  vendorName: string;
  vendorAddress: string;
  vendorVat: string;
  vendorPhone: string;
  vendorEmail: string;
  createdAt: string;
  expectedDeliveryDate: string;
  items: EditablePoItem[];
};

function buildEditablePo(data: VendorPurchaseOrderDto): EditablePo {
  const vendor = data.vendorId && typeof data.vendorId === 'object' ? data.vendorId : null;
  const rfq = data.rfqId && typeof data.rfqId === 'object' ? data.rfqId : null;
  return {
    _id: (data as any)._id || undefined,
    poNumber: data.poNumber || 'purchase-order',
    // Fall back to the linked RFQ's vessel name for legacy POs that pre-date the dedicated field.
    vesselName: data.vesselName || rfq?.vesselName || '',
    hideVesselPrefix: Boolean(data.hideVesselPrefix),
    generalRemarks: data.generalRemarks || '',
    vendorName: data.vendorName || vendor?.name || EMPTY_FALLBACK,
    vendorAddress: data.vendorAddress || vendor?.address || EMPTY_FALLBACK,
    vendorVat: data.vendorVat || EMPTY_FALLBACK,
    vendorPhone: data.vendorPhone || vendor?.phone || EMPTY_FALLBACK,
    vendorEmail: data.vendorEmail || vendor?.email || EMPTY_FALLBACK,
    createdAt: data.createdAt || '',
    // A stored day (its UTC midnight) read as that day once, so the header and the box agree (D109.6, D162).
    expectedDeliveryDate: storedDay(data.expectedDeliveryDate) ?? '',
    items: (data.items || []).map((it) => ({
      lineNumber: it.lineNumber,
      itemName: it.itemName || it.itemDescription || '',
      printedName: it.supplierItemName?.trim() || it.itemName || it.itemDescription || '',
      itemDescription: it.itemDescription || '',
      itemCode: it.itemCode || '',
      quantity: it.quantity != null ? String(it.quantity) : '',
      uom: it.uom || '',
      unitPrice: moneyInputText(it.unitPrice),
      totalPrice: moneyInputText(it.totalPrice),
      remarks: it.remarks || '',
      removed: it.removed === true,
    })),
  };
}

// The one label owner (BUSINESS_RULES#quotation-screen-money-labels):
// `Intl`'s own `currency: 'TTD'` style prints a bare "$", not "TT$" — the ICU
// data this Node ships has no distinct TT$ symbol, so the page header read as
// a US-dollar total (QA 2026-10-01). `ceil: false`: the figure is already
// finished server-side (`calcTotalPriceTtd`/`computeEditableTotals`), so this
// only labels and groups it, never re-rounds it. Called directly (the total here
// is always a number): `formatCurrency(n, 'TTD', { ceil: false })`.
// Unit Price / Total Price in the Line Items table are plain editable number
// inputs bound to a raw numeric string (`toNumberOrNull` parses it on save) —
// the label is a sibling, not part of the value, so it never reaches the
// parser. Same module, one label.
const TTD_PREFIX = currencyPrefix('TTD');

const formatDateUs = (value?: string) => {
  return fmtDate(value, EMPTY_FALLBACK);
};

// Hoisted out of `savePo` so `computeEditableTotals` below can share the same
// parsing rule rather than growing a second copy of it in this file.
const cleanOrEmpty = (value: string) => {
  const v = String(value || '').trim();
  return v === EMPTY_FALLBACK ? '' : v;
};
const toNumberOrNull = (value: string): number | null => {
  const cleaned = cleanOrEmpty(value);
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The Total Price this screen shows while an edit is in progress, over the ROWS
 * ON SCREEN (unsaved included) — not a re-fetch of the server's stored total,
 * which would go stale the moment a price cell changes. Same "missing is not
 * priced" rule the server's `vendorPurchaseOrder.service.calcTotalPriceTtd`
 * uses (TECH_DEBT #342): a line with neither a totalPrice nor a unitPrice x
 * quantity is left OUT of the sum, not read as $0, and counted so the note
 * below can say so. Never a second copy of the ARITHMETIC that function owns —
 * no ceiling, no currency conversion, both stay server-side — only the same
 * "what counts as priced" question, asked of the rows the user is looking at.
 * A `removed` line (D81) is in none of it, as in the server's `poTotals`.
 */
function computeEditableTotals(items: EditablePoItem[]): { total: number; unpricedLineCount: number } {
  let unpricedLineCount = 0;
  const total = items.filter((it) => !it.removed).reduce((sum, it) => {
    const totalPrice = toNumberOrNull(it.totalPrice);
    if (totalPrice != null) return sum + totalPrice;
    const unitPrice = toNumberOrNull(it.unitPrice);
    const quantity = toNumberOrNull(it.quantity);
    if (unitPrice != null && quantity != null) return sum + unitPrice * quantity;
    unpricedLineCount += 1;
    return sum;
  }, 0);
  return { total, unpricedLineCount };
}

// Item 11 (9 Oct QA retest): the client's own PO reference and the quotation's
// payment terms, read off the PO's linked confirmed order / quotation rather
// than duplicated onto the PO itself — neither field is stored there
// (ConfirmedOrder.clientPoNumber, Quotation.terms.paymentTerms are the owners).
// `refLabel` is the one owner for "read a flat field off a populated chain
// reference, or null when it is a raw id / absent" (`features/traceability/chainRef`).
function clientPoNumber(po: VendorPurchaseOrderDto): string | null {
  return refLabel(po.confirmedOrderId, ['clientPoNumber']);
}

function paymentTerms(po: VendorPurchaseOrderDto): string | null {
  const q = po.quotationId;
  const terms = q && typeof q === 'object' ? q.terms : null;
  const pt = terms && typeof terms.paymentTerms === 'string' ? terms.paymentTerms.trim() : '';
  return pt || null;
}

/**
 * Auto-generated PO numbers follow `${vessel}-${goods}-YYYYMMDD-${cat}-${tail}`
 * (see src/services/vendorPurchaseOrder.service.js → buildPoNumber). The vessel
 * portion is everything before the embedded YYYYMMDD date segment; the date
 * acts as a stable anchor regardless of how many dashes the vessel/goods
 * names contain after normalization.
 */
const VESSEL_PREFIX_PATTERN = /^.+?-(?=\d{8}-)/;

/** Strip the vessel portion from a PO number when the toggle is on and the format matches. */
function displayPoNumber(po: { poNumber: string; hideVesselPrefix?: boolean }): string {
  const full = po.poNumber || '';
  if (!po.hideVesselPrefix) return full;
  return full.replace(VESSEL_PREFIX_PATTERN, '');
}

const cellBase = 'min-h-[34px] px-3 py-1.5 border';
const cell = `${cellBase} text-sm text-slate-900`;
// The header type is the one every table header uses: 12px, semibold, slate (tableStyle.guard.test.ts).
const thCell = `${cellBase} bg-[#F9FAF9] text-left text-xs font-semibold text-slate-600`;
const tdCell = `${cell} bg-white`;
// Opaque on purpose: the first two columns are sticky (frozen-cols-2).
const pendingCell = `${cell} bg-amber-50`;
const LOCKED_INPUT = 'text-slate-600 cursor-not-allowed';
const PENDING_LOCK_HINT = 'Choose the case quantity below.';
// D81: a line whose order line left after the PO was sent.
const REMOVED_LINE_HINT = 'Removed from the order — the supplier is told it is no longer wanted.';
const WHOLE_PACKS_HINT = 'Enter a whole number of cases.';
// The sign rule's answers (`poLineAmountProblem`, the one owner the server's PUT asks too).
const AMOUNT_PROBLEM_HINT: Record<PoLineAmountProblem, string> = {
  quantity_negative: 'Quantity cannot be negative.',
  quantity_not_positive: 'Quantity must be above 0.',
  price_negative: 'Price cannot be negative.',
};
// B027 / B029: a PO with no lines greyed Export with no reason and still offered Send (the server
// now refuses VENDOR_PO_NO_LINES). Both buttons say why they are closed.
const NO_LINES_EXPORT_NOTE = 'This purchase order has no lines to export.';
const NO_LINES_SEND_NOTE = 'This purchase order has no lines to send.';

/**
 * D82: a line in the supplier's pack (`isPackApplied`) is ordered in WHOLE
 * packs — the PUT refuses 4.5 or 0 (VENDOR_PO_ROUNDING_QUANTITY_REQUIRED)
 * through the same owners used here (`isPackApplied`, `wholePackCount`, mirrored).
 */
function packCountInvalid(packConversion: VendorPoPackConversion | null | undefined, quantity: unknown): boolean {
  return isPackApplied(packConversion) && wholePackCount(Number(quantity)) == null;
}

/** The typed quantity and prices of one line against the stored line, through the shared sign rule. */
function lineAmountProblem(
  it: Pick<EditablePoItem, 'quantity' | 'unitPrice' | 'totalPrice'>,
  stored: { quantity?: number | null } | undefined
): PoLineAmountProblem | null {
  return poLineAmountProblem(
    { quantity: toNumberOrNull(it.quantity), unitPrice: toNumberOrNull(it.unitPrice), totalPrice: toNumberOrNull(it.totalPrice) },
    stored
  );
}

/**
 * One Vendor Details row, view-only (QA 2026-10-01: this block used to be
 * inline-editable; the owner asked for a look at the vendor a PO was raised
 * for, never a way to retype it from this screen). Same two-column grid and
 * label colour the editable rows used, with no input, no onChange and
 * nothing for Enter to save.
 */
function ReadOnlyField({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={`grid grid-cols-[100px_1fr] gap-2 ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 break-all">{value}</span>
    </div>
  );
}

export const VendorPurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canExport } = usePermission('vendor_purchase_orders');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [po, setPo] = useState<VendorPurchaseOrderDto | null>(null);
  const [editablePo, setEditablePo] = useState<EditablePo | null>(null);
  // D109.6: false while the delivery-date box holds a refused date (the PO still holds the OLD one).
  const [deliveryDateValid, setDeliveryDateValid] = useState(true);
  const [lineSearch, setLineSearch] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendWentNote, setSendWentNote] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [vendorMsg, setVendorMsg] = useState<{ error: boolean; text: string | null } | null>(null);
  const [converting, setConverting] = useState(false);
  const [convertMsg, setConvertMsg] = useState<{ error: boolean; text: string } | null>(null);
  // D169.7: what convert-all asks after it ran - round-ups to confirm (`offers`), or lines under a case to raise.
  const [convertAsk, setConvertAsk] = useState<{ content: DialogContent; offers: ConvertToCasesRoundUp[] } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // D170.3: an answer that turned a sent PO amended (a save, a pack decision, Convert all) offers to send it now, once
  // no round-up question is open or in flight (D169.7): one dialog at a time.
  const [offerResend, setOfferResend] = useState(false);
  const statusWas = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (statusWas.current === 'sent' && po?.status === 'amended') setOfferResend(true);
    statusWas.current = po?.status;
  }, [po?.status]);
  const resendPromptOpen = offerResend && !convertAsk && !converting;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchVendorPurchaseOrderById(id);
      setPo(data);
      setEditablePo(buildEditablePo(data));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load vendor purchase order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const updatePoItem = useCallback((idx: number, key: keyof EditablePoItem, value: string) => {
    const packLine = isPackApplied(po?.items?.[idx]?.packConversion);
    setEditablePo((prev) => {
      if (!prev) return prev;
      const nextItems = prev.items.map((row, rowIdx) => (rowIdx === idx ? { ...row, [key]: value } : row));
      if (key === 'quantity' || key === 'unitPrice') {
        // The server's rule on save (`vendorPurchaseOrder.keepServerOwnedLineFields`):
        // the typed unit is ceiled ONCE (the unit it stores), and the total is
        // ceil(that unit x qty) — so 1.111 x 5 previews 5.60, the figure saved,
        // not ceil(5.555) = 5.56. A blank quantity or price clears the total —
        // never a stated 0, which would count an unpriced line as priced at
        // nothing (TECH_DEBT #342). A line in the supplier's pack is stored at the
        // whole count the server reads (`typedPackCount`: 4.0000001 -> 4), so it
        // previews at that count.
        const typedQty = toNumberOrNull(nextItems[idx].quantity);
        const qty = packLine && typedQty != null ? wholePackCount(typedQty) ?? typedQty : typedQty;
        const unit = toNumberOrNull(nextItems[idx].unitPrice);
        nextItems[idx].totalPrice = qty != null && unit != null ? moneyInputText(ceilPrice(ceilPrice(unit) * qty)) : '';
      }
      return { ...prev, items: nextItems };
    });
  }, [po]);

  // `override`: a caller that has just changed the state (the vessel toggle) saves that
  // value, not the stale one this closure captured.
  const savePo = useCallback(async (override?: EditablePo) => {
    const current = override ?? editablePo;
    if (!id || !current) return;
    if (!deliveryDateValid) {
      setSaveStatus('error');
      setSaveError('Not saved: fix the delivery date first, it is not a real day.');
      return;
    }
    // A pack line says "whole cases" (which covers 0 and negatives); every other line gets the sign rule.
    if (current.items.some((it, i) => packCountInvalid(po?.items?.[i]?.packConversion, it.quantity))) {
      setSaveStatus('error');
      setSaveError(`Not saved: ${WHOLE_PACKS_HINT}`);
      return;
    }
    const badAmount = current.items
      .map((it, i) => lineAmountProblem(it, po?.items?.[i]))
      .find((problem) => problem != null);
    if (badAmount) {
      setSaveStatus('error');
      setSaveError(`Not saved: ${AMOUNT_PROBLEM_HINT[badAmount]}`);
      return;
    }
    setSaveStatus('saving');
    try {
      // cleanOrEmpty / toNumberOrNull are module-level now (see computeEditableTotals) —
      // this used to be a second, local copy of both.
      const updated = await updateVendorPurchaseOrder(id, {
        poNumber: cleanOrEmpty(current.poNumber),
        vesselName: current.vesselName,
        hideVesselPrefix: current.hideVesselPrefix,
        generalRemarks: current.generalRemarks,
        expectedDeliveryDate: current.expectedDeliveryDate || null,
        items: current.items.map((it) => ({
          // As stored, never invented: the PUT checks each position's lineNumber
          // against the stored line (null matching null), so `index + 1` for a
          // line stored without one refused every save of that PO.
          lineNumber: it.lineNumber,
          // D119.8: the printed name only. Typing the order's own wording back stores no
          // supplier name ('' clears it): the line then prints the order's wording.
          supplierItemName: it.printedName === it.itemName ? '' : cleanOrEmpty(it.printedName),
          itemDescription: cleanOrEmpty(it.itemDescription || ''),
          itemCode: cleanOrEmpty(it.itemCode),
          quantity: toNumberOrNull(it.quantity),
          uom: cleanOrEmpty(it.uom),
          unitPrice: toNumberOrNull(it.unitPrice),
          totalPrice: toNumberOrNull(it.totalPrice),
          remarks: cleanOrEmpty(it.remarks),
        })),
      });
      setPo(updated);
      setEditablePo(buildEditablePo(updated));
      setSaveStatus('saved');
      window.setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (e: unknown) {
      setSaveStatus('error');
      setSaveError(updateVendorPurchaseOrderErrorMessage(e));
      console.error('Failed to save purchase order:', e);
    }
  }, [id, editablePo, po, deliveryDateValid]);

  // A pack decision re-prices ONE line server-side. Take the returned PO as the
  // truth for that line, but keep any unsaved edits the buyer has typed on the
  // others — replacing every row would silently throw them away.
  const onPackDecided = useCallback((updated: VendorPurchaseOrderDto, lineIndex: number) => {
    setPo(updated);
    setEditablePo((prev) => {
      const fresh = buildEditablePo(updated);
      if (!prev || prev.items.length !== fresh.items.length) return fresh;
      return { ...prev, items: prev.items.map((row, i) => (i === lineIndex ? fresh.items[i] : row)) };
    });
  }, []);

  // Whether the page holds edits the server does not have yet: the WHOLE editable PO against the
  // stored one. Read by the Save button and by the vendor pick's discard warning.
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(editablePo) !== JSON.stringify(po ? buildEditablePo(po) : null),
    [editablePo, po]
  );

  // D86: the vendor is PICKED from the vendor list, never typed. The server writes vendorId and
  // the contact fields from the Vendor record (the one assign path, also used by the PO list);
  // a before-VAT PO's lines are rebuilt for that vendor, so the page takes the answer as the truth.
  const pickVendor = useCallback(async (next: VendorSelection) => {
    if (!id || !next.vendorId) return;
    // The assign rebuilds a before-VAT PO's lines for the vendor, replacing hand edits: ask first.
    // The WHOLE editable PO, not its lines alone: the assign replaces all of it with the server's.
    if (hasUnsavedChanges && !window.confirm('This purchase order has unsaved changes. Choosing a vendor rebuilds its lines for that vendor and discards them. Continue?')) return;
    setAssigning(true);
    setVendorMsg(null);
    try {
      const { purchaseOrder, notes } = await assignVendorToPurchaseOrder(id, next.vendorId);
      setPo(purchaseOrder);
      setEditablePo(buildEditablePo(purchaseOrder));
      setVendorMsg({ error: false, text: assignVendorNotesMessage(notes) });
    } catch (e: unknown) {
      setVendorMsg({ error: true, text: assignVendorToPurchaseOrderErrorMessage(e) });
    } finally {
      setAssigning(false);
    }
  }, [id, hasUnsavedChanges]);

  // D157.8: every line still in single items to whole cases, rounded up. The server rewrites any
  // line, so the page takes its answer whole (the button waits for unsaved edits to be saved).
  // D169.7: a catalogue part case is not rounded by itself. The first click asks (round up, or raise a line under
  // one case); a confirmed round-up is sent back as `roundUp`, and that answer asks nothing more.
  const convertAllToCases = useCallback(async (roundUp?: ConvertToCasesRoundUp[]) => {
    if (!id) return;
    setConverting(true);
    setConvertMsg(null);
    try {
      const { purchaseOrder, converted, skipped } = await convertPurchaseOrderToCases(id, roundUp);
      setPo(purchaseOrder);
      setEditablePo(buildEditablePo(purchaseOrder));
      setConvertMsg({ error: false, text: convertToCasesResultMessage(converted, skipped) });
      const ask = roundUp ? null : convertToCasesDialog(skipped);
      setConvertAsk(ask ? { content: ask, offers: convertToCasesRoundUpOffers(skipped) } : null);
    } catch (e: unknown) {
      // Refused like a pack decision: the same guard and codes.
      setConvertMsg({ error: true, text: decidePackRoundingErrorMessage(e) });
    } finally {
      setConverting(false);
    }
  }, [id]);

  // D161.5: the saved PO's PDF / Excel, the file its supplier gets, through the PO list's download.
  const exportPo = useCallback(async (format: 'pdf' | 'excel') => {
    if (!po) return;
    setExporting(true);
    setExportError(null);
    try {
      await downloadVendorPurchaseOrder(po, format);
    } catch (e: unknown) {
      setExportError(vendorPoSendErrorMessage(e, `${po.poNumber || 'This purchase order'} could not be exported. Try again.`));
    } finally {
      setExporting(false);
    }
  }, [po]);

  const handleEnterSave = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      await savePo();
    },
    [savePo]
  );

  const filteredItems = useMemo(() => {
    if (!editablePo?.items?.length) return [];
    const t = lineSearch.trim().toLowerCase();
    const withIndex = editablePo.items.map((it, originalIndex) => ({ it, originalIndex }));
    if (!t) return withIndex;
    return withIndex.filter(({ it }) => {
      const hay = [it.itemName, it.printedName, it.itemDescription, it.remarks, it.itemCode].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(t);
    });
  }, [editablePo, lineSearch]);

  if (loading) {
    return (
      <div className="h-full font-['Public_Sans',sans-serif]">
        <LoadingState text="Loading purchase order..." />
      </div>
    );
  }

  if (error || !po || !editablePo) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-red-600 font-['Public_Sans',sans-serif]">
        {error || 'Vendor purchase order not found'}
      </div>
    );
  }

  // Item 11: read off the linked confirmed order / quotation, never retyped
  // onto the PO (see the clientPoNumber/paymentTerms comment above).
  const clientPo = clientPoNumber(po);
  const terms = paymentTerms(po);
  // TECH_DEBT #342 — the rows on screen (unsaved edits included), not a re-fetch
  // of `po.totalPrice`/`po.unpricedLineCount`, which would go stale the moment a
  // price cell changes here. See computeEditableTotals.
  const editableTotals = computeEditableTotals(editablePo.items);
  // D82: the server refuses to send or render a PO while any line waits for a
  // case quantity; say so here instead of letting the click fail.
  const pendingPackLines = (po.items || []).filter((it) => it.packConversion?.status === 'pending').length;
  const pendingPackNote = pendingPackLines > 0 ? packDecisionPendingNote(pendingPackLines) : null;
  // D81 / D82: a pack decision (one line, or Convert all to cases) is made on a draft, sent or amended
  // PO; on a sent one it amends it (D170.3).
  const canDecidePacks = canEdit && ['draft', 'sent', 'amended'].includes(po.status);
  // D82: only a PO the server stamped 'before_vat' is priced before VAT; one
  // stored earlier carries the VAT-inclusive quotation cost (D63).
  const beforeVat = po.priceBasis === 'before_vat';

  return (
    <div className="h-full flex flex-col bg-brand-bg font-['Public_Sans',sans-serif] overflow-hidden">
      <div className="px-8 pt-4 pb-3 bg-brand-bg border-b border-slate-200 shrink-0">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <BackArrow label="Back to vendor purchase orders" onClick={() => navigate('/vendor-purchase-orders')} />
              {/* Item 11: the title is this purchase order's OWN number — it used to read
                  "RFQ: <rfq id>", which named the wrong document (the RFQ chip in the
                  chain rail just below already links to the RFQ itself). */}
              <h1 className="text-xl font-semibold leading-7 text-slate-900">
                {displayPoNumber(editablePo)}
              </h1>
              <StatusBadge status={po.status} />
              <ModuleInfoButton content={VENDOR_PO_DETAIL_INFO_CONTENT} ariaLabel="Vendor PO detail information" />
            </div>
            {/*
              This page knows its own order and what it was raised from. The
              vendor invoice hop is left unmentioned rather than shown empty:
              the Reconciliation panel below owns that relationship and fetches
              it separately, so claiming "none linked" from up here would be
              guessing.
            */}
            <ChainRail
              current="purchaseOrder"
              rfq={po.rfqId}
              quotation={po.quotationId}
              confirmedOrder={po.confirmedOrderId}
              purchaseOrders={[{ _id: po._id, poNumber: po.poNumber }]}
              className="pl-9"
            />
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-900 pl-9">
              <span>
                <span className="text-slate-500">Vendor:</span> {editablePo.vendorName || EMPTY_FALLBACK}
              </span>
              {/* Item 11: the PO's own copy — a vessel typed here never had anywhere to be read back from. */}
              <span>
                <span className="text-slate-500">Vessel:</span> {editablePo.vesselName || EMPTY_FALLBACK}
              </span>
              {/* Item 11: the client's own purchase-order reference (B6) — read off the
                  linked confirmed order, never duplicated onto the PO itself. */}
              <span>
                <span className="text-slate-500">Client PO:</span> {clientPo || EMPTY_FALLBACK}
              </span>
              {/* Item 11: the quotation's own terms — same reasoning as Client PO above. */}
              <span>
                <span className="text-slate-500">Terms:</span> {terms || EMPTY_FALLBACK}
              </span>
              <span>
                <span className="text-slate-500">Created:</span> {formatDateUs(editablePo.createdAt)}
              </span>
              {/* Item 11: a read-only glance up here; the one editable copy stays in
                  Vendor Details below (owner 2026-10-02) — one field, one writer. */}
              <span>
                <span className="text-slate-500">Delivery:</span> {formatDateUs(editablePo.expectedDeliveryDate)}
              </span>
              <span>
                {/* The live lines, as the server's totalItems counts them (D81). */}
                <span className="text-slate-500">Total Items:</span> {editablePo.items.filter((it) => !it.removed).length}
              </span>
              <span>
                <span className="text-slate-500">{beforeVat ? 'Total (before VAT):' : 'Total:'}</span> {formatCurrency(editableTotals.total, 'TTD', { ceil: false })}
                {editableTotals.unpricedLineCount > 0 && (
                  <span className="text-amber-700"> ({unpricedLineNote(editableTotals.unpricedLineCount)})</span>
                )}
              </span>
              {beforeVat && <span className="text-slate-500">{PRICES_EXCLUDE_VAT}</span>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <div
              className="flex items-center gap-2 h-9 px-3 rounded-md border bg-white min-w-[200px]"
              style={{ borderColor: BORDER }}
            >
              <Search className="w-4 h-4 text-slate-500 shrink-0" aria-hidden />
              <input
                value={lineSearch}
                onChange={(e) => setLineSearch(e.target.value)}
                placeholder="Search here"
                className="flex-1 min-w-0 text-sm bg-transparent outline-none placeholder:text-slate-400"
              />
            </div>
            {/* D161.5: the SAVED PO as its supplier gets it, so it waits for a save like Send. */}
            {canExport && (
              <ExportDropdown
                onExportPDF={() => void exportPo('pdf')}
                onExportExcel={() => void exportPo('excel')}
                disabled={!po.items?.length || pendingPackNote != null || hasUnsavedChanges || exporting}
                title={!po.items?.length ? NO_LINES_EXPORT_NOTE : pendingPackNote ?? (hasUnsavedChanges ? 'Save your changes first; the PO is exported as saved' : undefined)}
              />
            )}
            {po?.status === 'sent' && (
              <span className="text-xs text-emerald-600">
                Sent{po?.sentAt ? ` on ${fmtDateTime(po.sentAt)}` : ''}
              </span>
            )}
            {/* D81 / D170.3: changed after this PO was sent (its order, its supplier, or an edit here). */}
            {po?.status === 'amended' && (
              <span className="text-xs text-amber-700">
                Amended since it was sent (revision {po.revision ?? 0}) — send it to the vendor again
              </span>
            )}
            {canDecidePacks && (
              <button
                type="button"
                onClick={() => void convertAllToCases()}
                disabled={hasUnsavedChanges || converting}
                title={hasUnsavedChanges ? 'Save your changes first; the lines are converted as saved' : "Turn every line into whole cases of the supplier's pack; a part case is rounded up only once you confirm it"}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md border border-primary-500 bg-white text-primary-500 text-xs font-semibold hover:bg-primary-50 disabled:opacity-50"
              >
                {converting ? 'Converting...' : 'Convert all to cases'}
              </button>
            )}
            {/* QA 2026-10-08: Enter was the only way to save, and nothing on screen said so -- a
                delivery date picked from the calendar stayed unsaved. Same save as Enter. */}
            {canEdit && (
              <button
                type="button"
                onClick={() => void savePo()}
                disabled={!hasUnsavedChanges || saveStatus === 'saving'}
                title={hasUnsavedChanges ? 'Save the changes on this purchase order' : 'No changes to save'}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-50"
              >
                <Save className="w-4 h-4" aria-hidden />
                {saveStatus === 'saving' ? 'Saving...' : 'Save'}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => setShowSend(true)}
                disabled={pendingPackNote != null || hasUnsavedChanges || !po.items?.length}
                title={!po.items?.length ? NO_LINES_SEND_NOTE : pendingPackNote ?? (hasUnsavedChanges ? 'Save your changes first; the PO is sent as saved' : po?.status === 'sent' || po?.status === 'billed' ? `Already sent${po?.sentAt ? ` on ${fmtDateTime(po.sentAt)}` : ''} — this will resend it to the vendor` : undefined)}
                className={
                  po?.status === 'sent'
                    ? 'inline-flex items-center gap-2 h-9 px-4 rounded-md border border-primary-500 bg-white text-primary-500 text-xs font-semibold uppercase tracking-wide hover:bg-primary-50 disabled:opacity-50'
                    : 'inline-flex items-center gap-2 h-9 px-4 rounded-md bg-primary-500 text-white text-xs font-semibold uppercase tracking-wide hover:bg-primary-600 disabled:opacity-50'
                }
              >
                <Mail className="w-4 h-4" aria-hidden />
                {po?.status === 'sent' || po?.status === 'billed' ? 'Resend to vendor' : po?.status === 'amended' ? 'Send amendment' : 'Send to vendor'}
              </button>
            )}
            {pendingPackNote && (canEdit || canExport) && (
              <span className="text-xs text-amber-700">{pendingPackNote}</span>
            )}
            {exportError && <span className="text-xs max-w-md text-red-600">{exportError}</span>}
            {sendWentNote && <span role="alert" className="text-xs max-w-md text-red-600">{sendWentNote}</span>}
            {convertMsg && (
              <span className={`text-xs max-w-md ${convertMsg.error ? 'text-red-600' : 'text-slate-600'}`}>{convertMsg.text}</span>
            )}
            <span className={`text-xs min-w-[84px] max-w-xs ${saveStatus === 'error' ? 'text-red-600' : 'text-slate-500'}`}>
              {saveStatus === 'saving' && 'Saving...'}
              {saveStatus === 'saved' && 'Saved'}
              {saveStatus === 'error' && saveError}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 px-8 py-4 overflow-auto min-h-0">
        <div className="flex flex-col gap-4">
          <div
            className="rounded-lg border bg-white overflow-hidden"
            style={{ borderColor: BORDER }}
          >
            <div className="px-4 py-2 border-b text-sm font-medium text-slate-900" style={{ borderColor: BORDER }}>
              Vendor Details
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <ReadOnlyField label="PO Number" value={displayPoNumber(editablePo)} />

              {/* Vessel-in-export control: a toggle, not a text field to retype — it stays a button,
                  same as every other action in this header, while the fields beside it went
                  read-only. (QA hide-vessel ticket)
                  D119.6: on EVERY PO. A new VPO-##### number has no vessel segment to strip, but the
                  PDF / Excel "Vessel:" row still has to be switchable. It starts from the vendor's
                  "Hide vessel name on POs"; this PO's own choice overrides it. */}
              <div className="grid grid-cols-[100px_1fr] gap-2">
                  <span className="text-slate-500">Hide vessel name</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      // Not while a save is in flight: two PUTs answered out of order
                      // would leave the screen showing the opposite of what was stored.
                      disabled={!canEdit || saveStatus === 'saving'}
                      onClick={() => {
                        // Saved on the click: this toggle has no input, so Enter could never persist it
                        // (and a PO with no editable lines has nothing to press Enter in).
                        const next = { ...editablePo, hideVesselPrefix: !editablePo.hideVesselPrefix };
                        setEditablePo(next);
                        void savePo(next);
                      }}
                      title="When hidden, the vessel name is left off the PDF and Excel sent to the supplier (and removed from an old-style PO number)."
                      className={`px-3 py-1 rounded-md text-[13px] font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        editablePo.hideVesselPrefix
                          ? 'bg-white text-cyan-700 border-cyan-300 hover:bg-cyan-50'
                          : 'bg-cyan-600 text-white border-cyan-600 hover:bg-cyan-700'
                      }`}
                    >
                      {editablePo.hideVesselPrefix ? 'Unhide' : 'Hide'}
                    </button>
                    <span className="text-xs text-slate-400 font-mono truncate">
                      {editablePo.vesselName}
                    </span>
                  </div>
              </div>
              <ReadOnlyField label="Date" value={formatDateUs(editablePo.createdAt)} />
              {/* The one editable field here (owner 2026-10-02): the PUT is its only writer. */}
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="text-slate-500">Delivery</span>
                <DateInput
                  aria-label="Delivery date"
                  value={editablePo.expectedDeliveryDate}
                  onChange={(v) =>
                    setEditablePo((prev) => (prev ? { ...prev, expectedDeliveryDate: v } : prev))
                  }
                  validity={setDeliveryDateValid}
                  onKeyDown={handleEnterSave}
                  disabled={!canEdit}
                  title="Expected delivery / vessel ETA — used to sort the PO list"
                  className="text-slate-900 border border-slate-200 rounded px-2 py-1 text-sm"
                />
              </div>
              {/* A PO raised for a vendor keeps it (owner, 2026-10-02; the server
                  refuses VENDOR_PO_VENDOR_ALREADY_SET). Only a PO with no vendor
                  record — a typed supplier name — offers the picker. */}
              <div>
                {po.vendorId ? (
                  <ReadOnlyField label="Name" value={editablePo.vendorName} />
                ) : (
                  <VendorSelect
                    id="po-vendor"
                    label="Name"
                    value={{ vendorId: null, name: po.vendorName || '' }}
                    onChange={pickVendor}
                    allowClear={false}
                    disabled={!canEdit || assigning || po.status === 'sent' || po.status === 'amended' || po.status === 'billed'}
                  />
                )}
                {vendorMsg?.text && (
                  <p role="status" className={`mt-1 text-xs ${vendorMsg.error ? 'text-red-600' : 'text-amber-700'}`}>{vendorMsg.text}</p>
                )}
              </div>
              <ReadOnlyField label="Address" value={editablePo.vendorAddress} wide />
              {/* GSTIN removed from this screen (owner, 2026-10-01: not needed
                  for now) — `vendorGstin` is left untouched wherever it is
                  already stored; this view no longer reads or shows it. */}
              <ReadOnlyField label="VAT" value={editablePo.vendorVat} />
              <ReadOnlyField label="Phone" value={editablePo.vendorPhone} />
              <ReadOnlyField label="Email" value={editablePo.vendorEmail} />
              {/* D119.7: one note for the whole PO, printed on the PDF / Excel after the Vessel row.
                  Not the email message, not a line's remarks. Enter is a new line; Save saves it. */}
              <div className="grid grid-cols-[100px_1fr] gap-2 sm:col-span-2">
                <label htmlFor="po-general-remarks" className="text-slate-500">General remarks</label>
                <textarea
                  id="po-general-remarks"
                  rows={3}
                  aria-describedby="po-general-remarks-count"
                  value={editablePo.generalRemarks}
                  disabled={!canEdit}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEditablePo((prev) => (prev ? { ...prev, generalRemarks: v } : prev));
                  }}
                  placeholder="e.g. Can you hold this for tomorrow. Printed on the PO sent to the supplier."
                  className="text-slate-900 border border-slate-200 rounded px-2 py-1 text-sm w-full resize-y disabled:bg-slate-50"
                />
                {/* No maxLength: a paste is never cut silently (the supplier would read half a sentence).
                    The counter says so instead, and the server refuses a note past the limit. */}
                <div id="po-general-remarks-count" className="col-start-2 text-xs flex gap-2" aria-live="polite">
                  <span className={editablePo.generalRemarks.length > GENERAL_REMARKS_MAX ? 'text-red-600 font-medium' : 'text-slate-500'}>
                    {`${editablePo.generalRemarks.length} / ${GENERAL_REMARKS_MAX}`}
                  </span>
                  {editablePo.generalRemarks.length > GENERAL_REMARKS_MAX && (
                    <span className="text-red-600">{`Too long: the PO will not save past ${GENERAL_REMARKS_MAX} characters`}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-white overflow-hidden flex flex-col min-h-[200px]" style={{ borderColor: BORDER }}>
            <div className="px-4 py-2 border-b text-sm font-medium text-slate-900" style={{ borderColor: BORDER }}>
              Line Items
            </div>
            <div className="overflow-x-auto">
              {/* Line number and description stay put while price and remarks
                  scroll (`frozen-cols-*`, index.css). 48px is the first
                  <col> below, and the table is `table-fixed`, so that width is
                  exact rather than a suggestion. */}
              <table
                className="w-full min-w-[900px] border-collapse table-fixed frozen-cols-2"
                style={{ '--frozen-col-2': '48px', '--frozen-divider': BORDER } as React.CSSProperties}
              >
                <colgroup>
                  <col className="w-[48px]" />
                  <col className="w-[22%]" />
                  <col className="w-[120px]" />
                  <col className="w-[88px]" />
                  {/* Wide enough for the D82 pack note "(25 PCS/CS = 100 PCS)" under the Qty. */}
                  <col className="w-[140px]" />
                  <col className="w-[110px]" />
                  <col className="w-[110px]" />
                  <col className="w-[24%]" />
                </colgroup>
                <thead>
                  <tr>
                    <th className={`${thCell} border-t-0 border-l-0`} style={{ borderColor: BORDER }}>
                      #
                    </th>
                    <th className={thCell} style={{ borderColor: BORDER }}>
                      Description
                    </th>
                    <th className={thCell} style={{ borderColor: BORDER }}>
                      Item Code
                    </th>
                    <th className={thCell} style={{ borderColor: BORDER }}>
                      UOM
                    </th>
                    <th className={thCell} style={{ borderColor: BORDER }}>
                      Qty
                    </th>
                    <th className={thCell} style={{ borderColor: BORDER }}>
                      Unit Price
                    </th>
                    <th className={thCell} style={{ borderColor: BORDER }}>
                      Total Price
                    </th>
                    <th className={`${thCell} border-r-0`} style={{ borderColor: BORDER }}>
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 && (
                    <tr>
                      <td colSpan={8} className={`${tdCell} text-center py-8 text-slate-500 border-l-0`} style={{ borderColor: BORDER }}>
                        {!po.items?.length
                          ? 'No items on this purchase order.'
                          : 'No line items match your search.'}
                      </td>
                    </tr>
                  )}
                  {filteredItems.map(({ it, originalIndex }, idx) => {
                    const packConversion = po.items?.[originalIndex]?.packConversion;
                    const piecesNote = packPiecesNote({ quantity: toNumberOrNull(it.quantity), uom: it.uom, packConversion });
                    // D169.6: what one case holds, as the supplier's PDF prints it under the note.
                    const contentsNote = packContentsNote({ uom: it.uom, packConversion });
                    // D82: a pending line is answered only by the control under it
                    // (the round route prices the cases); a converted line keeps the
                    // supplier's pack as its unit. The PUT refuses both edits
                    // (VENDOR_PO_PACK_LINE_LOCKED) — this only stops offering them.
                    const pending = packConversion?.status === 'pending';
                    const uomLocked = pending || isPackApplied(packConversion);
                    const badPackCount = packCountInvalid(packConversion, it.quantity);
                    const amountProblem = lineAmountProblem(it, po.items?.[originalIndex]);
                    // A pack line already says "whole cases" (badPackCount); not two hints for one box.
                    const qtyProblem = !badPackCount && (amountProblem === 'quantity_negative' || amountProblem === 'quantity_not_positive') ? amountProblem : null;
                    // The sign rule says a price is negative; the hint sits on the box the person typed in. The total
                    // is theirs to type only while it is not worked out from the unit price and quantity.
                    const typedNegative = (v: string) => { const n = toNumberOrNull(v); return n != null && n < 0; };
                    const priceProblem = amountProblem === 'price_negative' && typedNegative(it.unitPrice) ? amountProblem : null;
                    const totalProblem = amountProblem === 'price_negative' && !priceProblem && typedNegative(it.totalPrice) ? amountProblem : null;
                    // D81: its order line left after the PO was sent; the server refuses a quantity on it.
                    const removed = po.items?.[originalIndex]?.removed === true;
                    // With both a price and a quantity the server derives the total
                    // (ceil of the ceiled unit x qty) on save; updatePoItem already shows
                    // that figure, so a typed total would only be thrown away.
                    const totalDerived = toNumberOrNull(it.unitPrice) != null && toNumberOrNull(it.quantity) != null;
                    return (
                    <React.Fragment key={`${it.lineNumber ?? originalIndex}-${originalIndex}`}>
                    <tr>
                      <td className={`${tdCell} border-l-0`} style={{ borderColor: BORDER }}>
                        {idx + 1}
                      </td>
                      <td className={tdCell} style={{ borderColor: BORDER }}>
                        {/* A textarea, not an input: a long name wraps instead of being cut off. */}
                        {/* D119.8: the box edits the name the supplier SEES (supplierItemName). */}
                        <textarea
                          rows={2}
                          value={it.printedName}
                          onChange={(e) => updatePoItem(originalIndex, 'printedName', e.target.value.replace(/\s*[\r\n]\s*/g, ' '))}
                          onKeyDown={handleEnterSave}
                          disabled={!canEdit}
                          className="w-full resize-none bg-transparent outline-none"
                        />
                        {/* The order's wording, read-only, only when the supplier is sent something else. */}
                        {it.itemName && it.itemName !== it.printedName && (
                          <div className="text-xs text-slate-500 break-words" title="As the order named it">{it.itemName}</div>
                        )}
                      </td>
                      <td className={tdCell} style={{ borderColor: BORDER }}>
                        <input
                          value={it.itemCode}
                          onChange={(e) => updatePoItem(originalIndex, 'itemCode', e.target.value)}
                          onKeyDown={handleEnterSave}
                  disabled={!canEdit}
                          className="w-full bg-transparent outline-none"
                        />
                      </td>
                      <td className={tdCell} style={{ borderColor: BORDER }}>
                        <input
                          value={it.uom}
                          onChange={(e) => updatePoItem(originalIndex, 'uom', e.target.value)}
                          onKeyDown={handleEnterSave}
                  disabled={!canEdit}
                          readOnly={uomLocked}
                          title={uomLocked ? (pending ? PENDING_LOCK_HINT : "The supplier's pack — change the case quantity instead.") : undefined}
                          className={`w-full bg-transparent outline-none ${uomLocked ? LOCKED_INPUT : ''}`}
                        />
                      </td>
                      <td className={tdCell} style={{ borderColor: BORDER }}>
                        <input
                          value={it.quantity}
                          onChange={(e) => updatePoItem(originalIndex, 'quantity', e.target.value)}
                          onKeyDown={handleEnterSave}
                  disabled={!canEdit}
                          readOnly={pending || removed}
                          title={pending ? PENDING_LOCK_HINT : removed ? REMOVED_LINE_HINT : undefined}
                          aria-invalid={badPackCount || qtyProblem != null || undefined}
                          className={`w-full bg-transparent outline-none ${pending ? LOCKED_INPUT : ''} ${badPackCount || qtyProblem ? 'text-red-600' : ''}`}
                        />
                        {qtyProblem && <div className="text-xs leading-tight text-red-600" role="alert">{AMOUNT_PROBLEM_HINT[qtyProblem]}</div>}
                        {pending && <div className="text-xs leading-tight text-amber-700">{PENDING_LOCK_HINT}</div>}
                        {removed && <div className="text-xs leading-tight text-red-600">{REMOVED_LINE_HINT}</div>}
                        {badPackCount && <div className="text-xs leading-tight text-red-600">{WHOLE_PACKS_HINT}</div>}
                        {piecesNote && <div className="text-xs leading-tight text-slate-500">{piecesNote}</div>}
                        {contentsNote && <div className="text-xs leading-tight text-slate-500">{contentsNote}</div>}
                      </td>
                      <td className={tdCell} style={{ borderColor: BORDER }}>
                        <div className="flex items-center gap-1">
                          <span className="shrink-0 text-slate-500 select-none">{TTD_PREFIX}</span>
                          <input
                            aria-label="Unit price (TT$)"
                            value={it.unitPrice}
                            onChange={(e) => updatePoItem(originalIndex, 'unitPrice', e.target.value)}
                            onKeyDown={handleEnterSave}
                            disabled={!canEdit}
                            aria-invalid={priceProblem != null || undefined}
                            className={`w-full min-w-0 bg-transparent outline-none ${priceProblem ? 'text-red-600' : ''}`}
                          />
                        </div>
                        {priceProblem && <div className="text-xs leading-tight text-red-600" role="alert">{AMOUNT_PROBLEM_HINT[priceProblem]}</div>}
                      </td>
                      <td className={tdCell} style={{ borderColor: BORDER }}>
                        <div className="flex items-center gap-1">
                          <span className="shrink-0 text-slate-500 select-none">{TTD_PREFIX}</span>
                          <input
                            aria-label="Total price (TT$)"
                            value={it.totalPrice}
                            onChange={(e) => updatePoItem(originalIndex, 'totalPrice', e.target.value)}
                            onKeyDown={handleEnterSave}
                            disabled={!canEdit}
                            readOnly={totalDerived}
                            title={totalDerived ? 'Unit price × quantity — change either to change the total.' : undefined}
                            aria-invalid={totalProblem != null || undefined}
                            className={`w-full min-w-0 bg-transparent outline-none ${totalDerived ? LOCKED_INPUT : ''} ${totalProblem ? 'text-red-600' : ''}`}
                          />
                        </div>
                        {totalProblem && <div className="text-xs leading-tight text-red-600" role="alert">{AMOUNT_PROBLEM_HINT[totalProblem]}</div>}
                      </td>
                      <td className={`${tdCell} border-r-0`} style={{ borderColor: BORDER }}>
                        <input
                          value={it.remarks}
                          onChange={(e) => updatePoItem(originalIndex, 'remarks', e.target.value)}
                          onKeyDown={handleEnterSave}
                  disabled={!canEdit}
                          title={it.remarks}
                          className="w-full bg-transparent outline-none"
                        />
                      </td>
                    </tr>
                    {/* D82: the pieces do not divide into the supplier's pack — a row
                        directly under the line asks for the case quantity. */}
                    {packConversion?.status === 'pending' && (
                      <tr>
                        <td className={`${pendingCell} border-l-0`} style={{ borderColor: BORDER }} />
                        <td colSpan={7} className={`${pendingCell} border-r-0`} style={{ borderColor: BORDER }}>
                          <PendingPackControl
                            poId={po._id}
                            lineIndex={originalIndex}
                            packConversion={packConversion}
                            canDecide={canDecidePacks}
                            onDecided={(updated) => onPackDecided(updated, originalIndex)}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {po?._id && (
            <ReconciliationPanel
              poId={po._id}
              poNumber={editablePo.poNumber}
              onOpenInvoice={(vendorInvoiceId) => navigate(CHAIN_PATH.vendorInvoice(vendorInvoiceId))}
              canEdit={canEdit}
            />
          )}
        </div>
      </div>

      {convertAsk && (
        <ActionDialog
          isOpen
          {...convertAsk.content}
          onClose={() => setConvertAsk(null)}
          onConfirm={convertAsk.offers.length
            ? async () => {
              const { offers } = convertAsk;
              setConvertAsk(null);
              await convertAllToCases(offers);
            }
            : undefined}
          // The single button of a notice wears `confirmLabel` too, so only a round-up offer names it.
          confirmLabel={convertAsk.offers.length ? 'Round up' : undefined}
          cancelLabel="Leave as is"
          busyLabel="Converting..."
        />
      )}

      <SendPurchaseOrderModal
        isOpen={showSend}
        onClose={() => setShowSend(false)}
        poNumber={displayPoNumber(editablePo)}
        sending={sending}
        poId={po?._id}
        alreadySent={po?.status === 'sent' || po?.status === 'billed'}
        sentAt={po?.sentAt}
        amendedRevision={po?.status === 'amended' ? po.revision ?? 0 : undefined}
        onSend={async (data) => {
          if (!po?._id) return;
          try {
            setSending(true);
            setSendWentNote(null);
            const updated = await sendVendorPurchaseOrder(po._id, data);
            setPo(updated);
            setEditablePo(buildEditablePo(updated));
            setShowSend(false);
          } catch (e: unknown) {
            const words = vendorPoSendErrorMessage(e, 'Failed to send purchase order');
            if (!emailAlreadyWent(e)) {
              window.alert(words);
              return;
            }
            // D180: the mail went, so the form closes. The sentence outlives this read, which may fail for the same reason.
            setSendWentNote(words);
            setShowSend(false);
            try {
              const fresh = await fetchVendorPurchaseOrderById(po._id);
              setPo(fresh);
              setEditablePo(buildEditablePo(fresh));
            } catch (readErr: unknown) {
              console.error('PO could not be re-read after its send:', readErr);
            }
          } finally {
            setSending(false);
          }
        }}
      />

      <ActionDialog
        isOpen={resendPromptOpen}
        tone="confirm"
        title={`This PO was already sent to ${editablePo.vendorName}. Send the amended PO now?`}
        confirmLabel="Send now"
        cancelLabel="Later"
        onClose={() => setOfferResend(false)}
        onConfirm={() => {
          setOfferResend(false);
          setShowSend(true);
        }}
      />
    </div>
  );
};
